
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PEOPLE } from '../data/GET_PEOPLE';

/** `?tags=3,7` — kept in the URL alongside `search` so a filtered list is linkable. */
const TAGS_PARAM = 'tags';

/**
 * `?missingConsent=1` — the registration follow-up worklist.
 *
 * In the URL for the same reason as the others: this list is something a centre manager works
 * through over days and will want to bookmark or send to someone.
 */
const MISSING_CONSENT_PARAM = 'missingConsent';

/**
 * `?canLogin=1` / `?canLogin=0` — three states, so absent is not the same as off.
 *
 * Unset means everybody; `1` is people with a password; `0` is the more useful worklist —
 * people with a membership who have never set one, which is what an unaccepted invitation or
 * an imported record looks like from the outside. A plain toggle could not say that.
 */
const CAN_LOGIN_PARAM = 'canLogin';

const parseTagIds = (params: URLSearchParams): number[] =>
    (params.get(TAGS_PARAM) ?? '')
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

const parseMissingConsent = (params: URLSearchParams): boolean =>
    params.get(MISSING_CONSENT_PARAM) === '1';

const parseCanLogin = (params: URLSearchParams): boolean | null => {
    const raw = params.get(CAN_LOGIN_PARAM);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
};

function usePeopleSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get('search') || ''
    );
    const [tagIds, setTagIds] = useState<number[]>(
        () => parseTagIds(new URLSearchParams(location.search))
    );
    const [missingConsent, setMissingConsent] = useState<boolean>(
        () => parseMissingConsent(new URLSearchParams(location.search))
    );
    const [canLogin, setCanLogin] = useState<boolean | null>(
        () => parseCanLogin(new URLSearchParams(location.search))
    );
    const [hasSearched, setHasSearched] = useState(false);
    const { loading, error, data, refetch } = useQuery(GET_PEOPLE, {
        variables: { search, tagIds, missingConsent, canLogin },
    });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Every filter lives in one query string, so any one of them changing has to carry the rest.
    const buildSearchString = useCallback(
        (
            nextSearch: string,
            nextTagIds: number[],
            nextMissingConsent: boolean,
            nextCanLogin: boolean | null,
        ) => {
            const params = new URLSearchParams();
            params.set('search', nextSearch);
            if (nextTagIds.length > 0) params.set(TAGS_PARAM, nextTagIds.join(','));
            if (nextMissingConsent) params.set(MISSING_CONSENT_PARAM, '1');
            if (nextCanLogin !== null) params.set(CAN_LOGIN_PARAM, nextCanLogin ? '1' : '0');
            return `?${params.toString()}`;
        },
        []
    );

    const handleSearchChange = useCallback((_: any, change: any) => {
        if (!hasSearched) setHasSearched(true);
        clearTimeout(timeoutRef.current);
        setSearch(change.value);
        timeoutRef.current = setTimeout(() => {
            navigate({
                pathname: location.pathname,
                search: buildSearchString(change.value, tagIds, missingConsent, canLogin),
            });
            refetch({ search: change.value, tagIds, missingConsent, canLogin });
        }, 500);
    }, [refetch, hasSearched, navigate, location.pathname, tagIds, missingConsent, canLogin, buildSearchString]);

    // Picking from a dropdown is a deliberate act, not typing — so no debounce here; the
    // 500ms wait that stops a keystroke-per-request would just feel like lag.
    const handleTagsChange = useCallback((nextTagIds: number[]) => {
        clearTimeout(timeoutRef.current);
        setTagIds(nextTagIds);
        navigate({
            pathname: location.pathname,
            search: buildSearchString(search, nextTagIds, missingConsent, canLogin),
        });
        refetch({ search, tagIds: nextTagIds, missingConsent, canLogin });
    }, [refetch, navigate, location.pathname, search, missingConsent, canLogin, buildSearchString]);

    const handleMissingConsentChange = useCallback((next: boolean) => {
        clearTimeout(timeoutRef.current);
        setMissingConsent(next);
        navigate({
            pathname: location.pathname,
            search: buildSearchString(search, tagIds, next, canLogin),
        });
        refetch({ search, tagIds, missingConsent: next, canLogin });
    }, [refetch, navigate, location.pathname, search, tagIds, canLogin, buildSearchString]);

    const handleCanLoginChange = useCallback((next: boolean | null) => {
        clearTimeout(timeoutRef.current);
        setCanLogin(next);
        navigate({
            pathname: location.pathname,
            search: buildSearchString(search, tagIds, missingConsent, next),
        });
        refetch({ search, tagIds, missingConsent, canLogin: next });
    }, [refetch, navigate, location.pathname, search, tagIds, missingConsent, buildSearchString]);

    // Sync state with URL on browser back/forward.
    //
    // The dependency array is deliberately just `[location.search, refetch]`, even though the
    // body reads every filter: the effect's whole job is to detect that the *URL* has moved out
    // of step with state, so re-running it whenever state changes would have it fight the
    // handlers that just set that state. The lint warning naming the filters as missing
    // dependencies is expected, and predates `canLogin` joining the list.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const param = params.get('search');
        const urlTagIds = parseTagIds(params);
        const urlMissingConsent = parseMissingConsent(params);
        const urlCanLogin = parseCanLogin(params);
        const tagsDiffer = urlTagIds.join(',') !== tagIds.join(',');
        const consentDiffers = urlMissingConsent !== missingConsent;
        const canLoginDiffers = urlCanLogin !== canLogin;

        if (param === null) {
            setSearch('');
            setHasSearched(false);
            if (tagsDiffer) setTagIds(urlTagIds);
            if (consentDiffers) setMissingConsent(urlMissingConsent);
            if (canLoginDiffers) setCanLogin(urlCanLogin);
            refetch({ search: '', tagIds: urlTagIds, missingConsent: urlMissingConsent, canLogin: urlCanLogin });
        } else if (param !== search || tagsDiffer || consentDiffers || canLoginDiffers) {
            setSearch(param);
            setHasSearched(true);
            if (tagsDiffer) setTagIds(urlTagIds);
            if (consentDiffers) setMissingConsent(urlMissingConsent);
            if (canLoginDiffers) setCanLogin(urlCanLogin);
            refetch({ search: param, tagIds: urlTagIds, missingConsent: urlMissingConsent, canLogin: urlCanLogin });
        }
    }, [location.search, refetch]);

    const hasTagFilter = tagIds.length > 0;

    // A tag filter can legitimately return few or no people, and the search box has to stay
    // on screen while it does — otherwise narrowing to an empty result removes the control
    // you need to widen it again. The consent filter has exactly the same property.
    const shouldShowSearch =
        (data?.users.length > 10) || search.length > 0 || hasSearched || hasTagFilter
        || missingConsent || canLogin !== null;

    /**
     * Whether any advanced filter is on. The disclosure starts open when one is, so a link
     * someone was sent — or a back-button return — does not hide the very filter producing
     * the list they are looking at.
     */
    const hasAdvancedFilter = missingConsent || canLogin !== null;

    return {
        search,
        tagIds,
        missingConsent,
        canLogin,
        hasTagFilter,
        hasAdvancedFilter,
        loading,
        error,
        data,
        handleSearchChange,
        handleTagsChange,
        handleMissingConsentChange,
        handleCanLoginChange,
        shouldShowSearch,
    };
}

export { usePeopleSearch };
